// 2026-05-26 - FormuLearning - Step 1 API 호출
// 2026-09-26 수정 - 이슈 #95 단계 3-5: 소비처 없는 비스트림 검색 래퍼 `searchStep1` 삭제(서버 경로는 존치).
// 파일 위치: frontend/src/api/step1.ts
// 2026-09-17 수정 - 이슈 #79(M915-03): 검색 스트림에 **검색 ID·중단 신호**를 싣고 중단 요청 API 를 둔다.
// 2026-09-23 수정 - 이슈 #86: 대조약 상세 보강에 `item_seq` 전달 + 같은 품목 진행 중 요청 공유.
// 2026-09-23 수정 - 이슈 #87: `PatentMeta.fetch_failed`(KIPRIS 조회 실패 검색어).
// 2026-09-23 수정 - 이슈 #73: 대조약·허가 품목 건수 조회(`fetchRldCount`)·병합 수집(`collectRld`).
// 2026-09-24 수정 - 이슈 #75(M915-04): `substance_fixed_at` 필드 · 주성분 확정 `fixSubstance`(BR-STEP-03).

import client from './client'
import type { ApiResponse } from '../types/api'

export interface Step1Data {
  id: string
  project_id: string
  search_input: string | null
  resolved_en: string | null
  resolved_ko: string | null
  common_name: string | null
  iupac_name: string | null
  canonical_smiles: string | null
  inchi: string | null
  cas_number: string | null
  molecular_image: string | null
  manufacturers: Record<string, string | null>[]
  rld_list: Record<string, unknown>[]
  selected_rld_id: number | null
  patents: Record<string, string | null>[]
  patent_meta: PatentMeta
  /** 식약처 의약품 특허정보(#105, M1002-21) — KIPRIS 와 별도. null·없음 = 이 기능 이전 검색 */
  mfds_patents?: MfdsPatents | null
  adme: Record<string, unknown>
  ai_summary: string | null
  ai_summary_ko: string | null
  current_snapshot_id: string | null
  /** 주성분 확정 시각(BR-STEP-03, #75). 있으면 재검색·이력 복원을 화면이 막는다. */
  substance_fixed_at?: string | null
  // G3 뷰 하이드레이션(이슈 #33) — 뷰에 영속 컬럼이 없어 getStep1이 현재 스냅샷에서 조인해 실어준다.
  pubchem_extra?: Record<string, unknown> | null
  sources?: string[]
  created_at: string
  updated_at: string
}

export interface PatentMeta {
  total_search_count?: number | null
  registered_found?: number | null
  shown?: number | null
  // 2026-09-09 - 이슈 #72(M901-09): 분류 성패. 화면이 «분류가 원래 없는 것»과
  //   «분류에 실패한 것»을 구분해야 한다 — 종전엔 둘 다 빈칸으로 보였다.
  classified?: number | null
  classify_failed?: number | null
  // 2026-09-23 - 이슈 #87: KIPRIS 가 응답하지 않은 검색어. «조회 실패»를 «결과 없음»과 구분한다.
  fetch_failed?: string[] | null
}

// 2026-11-03 - 이슈 #105(M1002-21): 식약처 의약품 특허정보. 행 = 특허 1건(품목×특허 응답을 묶음).
export interface MfdsPatentProduct {
  item_seq: string
  item_name: string | null
  maker: string | null
  ingredient: string | null
  pms_end_date: string | null
}

export interface MfdsPatent {
  patent_no: string | null // 없으면 미등록 출원(거절·취하·심사중 …)
  title: string | null
  holder: string | null
  status: string | null
  expiry_date: string | null
  type: string | null // 특허 구분(`물질, 물질(염)` …)
  page_gb: string | null // `제품특허`(품목 등재) / `기타특허`
  registered: boolean
  products: MfdsPatentProduct[]
  product_count: number
}

export interface MfdsPatents {
  patents: MfdsPatent[]
  meta: {
    query: string | null
    param: string | null
    rows: number
    total_rows: number
    patent_count: number
    product_patent_count: number
    fetch_failed: boolean
    failed_queries: string[]
  }
}

export interface SearchResult {
  molecule: Record<string, string | null> | null
  manufacturers: Record<string, string | null>[]
  rld_list: Record<string, unknown>[]
  patents: Record<string, string | null>[]
  patent_meta?: PatentMeta
  adme: Record<string, unknown> | null
  ai_summary: string | null
  ai_summary_ko?: string | null
  sources: string[]
  resolved_en?: string | null
  resolved_ko?: string | null
  pubchem_extra?: Record<string, unknown> | null
}

// 정보/뷰 분리(이슈 #6) — 검색 스냅샷 이력
export interface SnapshotListItem {
  id: string
  search_input: string | null
  resolved_en: string | null
  resolved_ko: string | null
  sources: string[]
  fetched_at: string
}

export async function listSnapshots(projectId: string): Promise<SnapshotListItem[]> {
  const { data } = await client.get<ApiResponse<SnapshotListItem[]>>(`/step1/${projectId}/snapshots`)
  return data.data ?? []
}

export interface SnapshotDetail extends SnapshotListItem {
  molecule: Record<string, unknown>
  manufacturers: Record<string, string | null>[]
  rld_list: Record<string, unknown>[]
  patents: Record<string, string | null>[]
  patent_meta: PatentMeta
  adme: Record<string, unknown>
  ai_summary: string | null
}

export async function getSnapshot(projectId: string, snapshotId: string): Promise<SnapshotDetail> {
  const { data } = await client.get<ApiResponse<SnapshotDetail>>(`/step1/${projectId}/snapshots/${snapshotId}`)
  return data.data!
}

export async function restoreSnapshot(projectId: string, snapshotId: string): Promise<Step1Data> {
  const { data } = await client.post<ApiResponse<Step1Data>>(`/step1/${projectId}/snapshots/${snapshotId}/restore`)
  return data.data!
}

export async function getStep1(projectId: string): Promise<Step1Data> {
  const { data } = await client.get<ApiResponse<Step1Data>>(`/step1/${projectId}/`)
  return data.data!
}

export async function updateStep1(projectId: string, body: Partial<Step1Data>): Promise<Step1Data> {
  const { data } = await client.put<ApiResponse<Step1Data>>(`/step1/${projectId}/`, body)
  return data.data!
}

// 주성분 확정(BR-STEP-03, #75) — 멱등. 이미 확정됐으면 최초 시각이 돌아온다.
export async function fixSubstance(projectId: string): Promise<string> {
  const { data } = await client.post<ApiResponse<{ substance_fixed_at: string }>>(`/step1/${projectId}/substance/fix`)
  return data.data!.substance_fixed_at
}

// RLD 지연 보강 — 품목의 허가정보 상세를 온디맨드로 가져온다(이미 보강 시 그대로 반환).
// itemSeq 를 주면 서버가 저장본·진행 중 검색 목록에서 그 품목을 찾는다 — 검색 중에도 올바른 제품(#86).
// 허가정보 상세 조회 실패는 `enriched=false`·`detail_status='failed'` 로 온다(재시도 대상).
// 같은 품목 요청이 진행 중이면 그 Promise 를 공유한다 — 대조약 선택이 목록(RldSection)·카드(Step1Card)
// 양쪽에서 같은 품목을 동시에 부르던 중복 요청을 없앤다(#86).
const rldDetailInflight = new Map<string, Promise<Record<string, unknown>>>()

export function fetchRldDetail(projectId: string, index: number, itemSeq?: string | null): Promise<Record<string, unknown>> {
  const key = `${projectId}:${itemSeq ?? `#${index}`}`
  const pending = rldDetailInflight.get(key)
  if (pending) return pending
  const request = client
    .post<ApiResponse<Record<string, unknown>>>(`/step1/${projectId}/rld/${index}/detail`, null, {
      params: itemSeq ? { item_seq: itemSeq } : undefined,
    })
    .then(({ data }) => data.data!)
    .finally(() => rldDetailInflight.delete(key))
  rldDetailInflight.set(key, request)
  return request
}

/** 대조약·허가 품목 건수(#73 U1) — 목록을 모으기 전에 규모를 알린다. 조회 실패한 쪽은 null.
 *  `permit_total` 은 취소·만료 포함 상한이다. `threshold` 이상이면 화면이 확인을 받고 모은다. */
export interface RldCount {
  reference_total: number | null
  permit_total: number | null
  english: string | null
  threshold: number
}

export async function fetchRldCount(projectId: string): Promise<RldCount> {
  const { data } = await client.get<ApiResponse<RldCount>>(`/step1/${projectId}/rld/count`)
  return data.data!
}

/** 대조약 + 허가 품목 목록 수집·병합(#73, M915-05). 저장된 Step 1 전체를 돌려준다 — 선택은 같은 품목의 새 위치.
 *  진행 중 검색이 있으면 409, 허가정보 목록 조회 실패는 502. */
export async function collectRld(projectId: string): Promise<Step1Data> {
  const { data } = await client.post<ApiResponse<Step1Data>>(`/step1/${projectId}/rld/collect`)
  return data.data!
}

/** 검색 입력 타입 (M84-40, 이슈 #61) — 시스템이 입력값 종류를 추정하다 오판하던 것을 없앤다.
 *  PubChem 조회 경로가 갈린다: SMILES 는 전용 경로가 필요하고, CAS 는 이름 경로가 해석한다. */
export type SearchInputType = 'name' | 'smiles' | 'cas'

// 점진적 표시(이슈 #4) — SSE 스트림 이벤트
// 'cancelled' — 중단 요청(#79)으로 무효가 된 검색을 서버가 다음 단계 전에 멈췄다.
export interface StreamEvent {
  event: 'stage' | 'panel' | 'done' | 'error' | 'cancelled'
  data: Record<string, unknown>
}

/** 검색 ID 발급(#79) — 중단 요청이 어느 검색을 가리키는지 서버와 맞추는 식별자.
 *  `crypto.randomUUID()` 는 보안 컨텍스트(HTTPS·localhost)에서만 있어 `getRandomValues` 로 v4 를 만든다. */
export function newSearchId(): string {
  const b = crypto.getRandomValues(new Uint8Array(16))
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

/** SSE로 단계별 검색 결과를 수신한다. 각 이벤트마다 onEvent 콜백을 호출.
 *  EventSource는 Authorization 헤더를 못 실어 fetch ReadableStream으로 파싱한다.
 *  `signal` 로 끊으면 `AbortError` 로 거부된다 — 호출부가 자기 검색인지 가려 처리한다(#79). */
export async function searchStep1Stream(
  projectId: string,
  query: string,
  onEvent: (ev: StreamEvent) => void,
  inputType: SearchInputType = 'name',
  opts: { searchId?: string; signal?: AbortSignal } = {},
): Promise<void> {
  const token = localStorage.getItem('access_token')
  const resp = await fetch(`/api/v1/step1/${projectId}/search/stream`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ query, input_type: inputType, search_id: opts.searchId }),
    signal: opts.signal,
  })
  if (!resp.ok || !resp.body) {
    throw new Error(`search stream failed: HTTP ${resp.status}`)
  }
  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    // SSE 메시지는 빈 줄(\n\n)로 구분. 각 메시지의 'data:' 라인을 추출.
    let sep: number
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const raw = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      const line = raw.split('\n').find((l) => l.startsWith('data:'))
      if (!line) continue
      try {
        onEvent(JSON.parse(line.slice(5).trim()) as StreamEvent)
      } catch {
        // 부분/비정상 청크는 무시
      }
    }
  }
}

/** 검색 중단 요청(#79 Break). 서버가 검색 ID 를 무효 처리하고 결과를 알려 준다.
 *  - `cancelled`: 저장 전에 멈췄다 → 화면을 저장본으로 되돌린다
 *  - `completed`: 이미 저장이 끝났다(저장 중이었다면 끝날 때까지 기다린 뒤 응답) → 새 결과를 보인다 */
export async function cancelStep1Search(projectId: string, searchId: string): Promise<'cancelled' | 'completed'> {
  const { data } = await client.post<ApiResponse<{ status: 'cancelled' | 'completed' }>>(
    `/step1/${projectId}/search/${searchId}/cancel`)
  return data.data!.status
}
